import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  HStack,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react';
import { MoreHorizontal, MessageSquareText } from 'lucide-react';
import CommentReactionButtons from '../CommentReactionButtons';
import CommentReactionModal from '../CommentReactionModal';
import LexicalCommentViewer from '../../../../LexicalCommentViewer';
import { CommentEditor } from '../../../../CommentEditor';
import { SharedHistoryContext } from '../../../../context/SharedHistoryContext';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import TwygsEditorNodes from '../../../../nodes/TwygsEditorNodes';
import getTheme from '../../../../themes/TwygsEditorTheme';
import { convertTimeCreatedAt } from '../../../../../../constants';
import { handleUserClick } from '../../../../../../utils/helper.util';
import VerifiedIcon from '../../../../../../assets/verified-icon.svg';
import FoundingCreatorIcon from '../../../../../../assets/founding_creator_icon.png';
import FlagIcon from '../../../../../../assets/flag.svg';
import EditIcon from '../../../../../../assets/edit-square-black-icon.svg';
import DeleteIcon from '../../../../../../assets/trash-icon.svg';
import ToastBtnMessage from '../../../../../ToastBtnMessage';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import ReportModal from '../ReportModal';
import DeleteModalIcon from '../../../../../../assets/delete-modal-icon.svg';
import UserAvatar from '../../../../../../assets/medium_default_optimized.png';
import CustomModal from '../../../../../Modal/CustomModal';
import RankBadge from '../../../../../../components/RankBadge';
import AwardWriterModal from '../../../../../../components/AwardWriterModal';
import { useNavigate } from 'react-router-dom';
import { createReputation } from 'src/apis/article.api';
import { commentApi } from '../../../../../../apis/comment.api';
import { useMutation } from '@tanstack/react-query';
import AwardModalOnSidePanel from '../AwardWriterModalSidebar';
import useRankBadgeTooltip from '../../../../../../hooks/useRankBadgeTooltip';
import { WriterBadgeInfo } from '../../../../../../components/WriterBadgeInfo';
import SensitiveContentCard from '../../../../../../components/SensitiveContentCard';
import ReputationPointComponent from '../../../../../../components/ReputationPointComponent';
import DataLayer from '../../../../../../utils/data-layer';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';
import './styles.css';

interface ThreadedReplyItemProps {
  comment: any;
  currentUser: any;
  createComment: (data: any) => Promise<any>;
  updateComment: (data: any) => Promise<any>;
  deleteComment: (commentId: string) => Promise<any>;
  reactToComment: (data: any) => Promise<any>;
  fetchReplies: () => Promise<void>;
  refetchComments: () => void;
  article: any;
  articleId: string;
  nodeId?: string;
  convertLexicalScope: (data: any) => Promise<any>;
  modalType: string;
  depth?: number;
  maxDepth?: number;
  onReplyAdded?: (reply: any) => void;
  isLastChild?: boolean;
  parentAvatarCenter: number;
  parentColumnWidth: number;
}

// Layout constants for replies
const REPLY_AVATAR_SIZE = 28;
const REPLY_AVATAR_COLUMN_WIDTH = 36;
const REPLY_AVATAR_CENTER = REPLY_AVATAR_SIZE / 2; // 14px

const ThreadedReplyItem: React.FC<ThreadedReplyItemProps> = ({
  comment,
  currentUser,
  createComment,
  updateComment,
  deleteComment,
  reactToComment,
  fetchReplies,
  refetchComments,
  article,
  articleId,
  nodeId,
  convertLexicalScope,
  modalType,
  depth = 1,
  maxDepth = 3,
  onReplyAdded,
  isLastChild = false,
  parentAvatarCenter,
  parentColumnWidth
}) => {
  const userId = JSON.parse(localStorage.getItem('user') || '{}')?.id;
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [open, setOpen] = useState(false);
  const [showSensitiveContentCard, setShowSensitiveContentCard] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [openAwardWriterModal, setOpenAwardWriterModal] = useState(false);
  const [isCreatingComment, setIsCreatingComment] = useState(false);
  const [reactionCounts, setReactionCounts] = useState({});
  const [isReactionModalOpen, setIsReactionModalOpen] = useState(false);
  const [children, setChildren] = useState(comment.children || []);

  const {
    openPopup,
    totalAwardedPoints,
    isTouchDevice,
    handleMouseEnter,
    handleClick,
    handleMouseLeave
  } = useRankBadgeTooltip();
  const toast = useToast();
  const navigate = useNavigate();

  const editorStateRef = React.useRef(null);
  const { mutateAsync: createPoint, isPending: isCreatingPoint } = useMutation({
    mutationFn: createReputation
  });

  const initialEditorState = comment.content
    ? JSON.stringify(comment?.content?.editorState)
    : undefined;

  const initialConfig = {
    editorState: isReplying ? null : initialEditorState,
    namespace: 'Twygs-comment-editor-threaded-reply',
    nodes: [...TwygsEditorNodes],
    onError: (error: Error) => {
      throw error;
    },
    theme: getTheme(true)
  };

  useEffect(() => {
    const loadReactionCounts = async () => {
      try {
        const reactionBreakdown = await commentApi.getCommentReactionBreakdown(comment.id);
        setReactionCounts(reactionBreakdown);
      } catch (error) {
        console.error('Failed to load reaction counts:', error);
      }
    };

    if (comment.id) {
      loadReactionCounts();
    }
  }, [comment.id, comment.reactionNumber, comment.reactedType]);

  useEffect(() => {
    setChildren(comment.children || []);
  }, [comment.children]);

  const handleEdit = async () => {
    setIsCreatingComment(true);
    if (editorStateRef.current) {
      const convertedLexicalScope = await convertLexicalScope({
        editorState: editorStateRef.current
      });
      try {
        await updateComment({
          commentId: comment.id,
          content: convertedLexicalScope
        });
        await fetchReplies();
        setIsEditing(false);
      } catch (error) {
        console.error('Failed to edit comment:', error);
      } finally {
        setIsCreatingComment(false);
      }
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleted(true);
      await deleteComment(comment.id);
      setOpen(false);
      toast({
        render: () => (
          <ToastBtnMessage
            title={'Comment successfully deleted!'}
            isError={false}
            color="jet-black"
            isHiddenButton
          />
        ),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top'
      });
    } catch (error) {
      console.error('Failed to delete comment:', error);
      setIsDeleted(false);
    }
  };

  const handleReply = async (parentId: string) => {
    setIsCreatingComment(true);
    if (editorStateRef.current) {
      const convertedLexicalScope = await convertLexicalScope({
        editorState: editorStateRef.current
      });
      try {
        const result = await createComment({
          articleId,
          nodeId,
          parentCommentId: parentId,
          content: convertedLexicalScope
        });
        if (result) {
          setChildren((prev) => [...prev, result]);
          onReplyAdded?.(result);

          if (article) {
            const articleType = article.writingType?.name;
            DataLayer.trackArticleComment(articleType, articleId);
          }

          refetchComments();
          setIsReplying(false);
          editorStateRef.current = null;
        }
      } catch (error) {
        console.error('Failed to post reply:', error);
      } finally {
        setIsCreatingComment(false);
      }
    }
  };

  const handleReaction = async (commentId: string, reactionType: string, isLike: boolean) => {
    try {
      if (!userId) return UnauthorizedDispatcher();
      const result = await reactToComment({
        commentId,
        nodeId,
        reactionType,
        isLike
      });

      comment.isReacted = result.isReacted;
      comment.reactedType = result.reactType;
      comment.reactionNumber = result.reactionCount;
    } catch (error) {
      console.error('Failed to react to comment:', error);
    }
  };

  const handleCreatePoint = async (point: any) => {
    try {
      await createPoint({
        amount: point,
        recipientId: comment.user.id,
        entityId: comment.id,
        entityType: 'comment'
      });

      DataLayer.trackCommentAward(article?.writingType?.name, articleId);

      await fetchReplies();
      toast({
        render: () => (
          <ToastBtnMessage
            title="Successfully awarded the comment!"
            isError={false}
            color="jet-black"
            isHiddenButton
          />
        ),
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
      setOpenAwardWriterModal(false);
    } catch (error: any) {
      toast({
        render: () => (
          <ToastBtnMessage
            title={error?.response?.data?.message}
            isError={true}
            color="jet-black"
            isHiddenButton
          />
        ),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
    }
  };

  useEffect(() => {
    if (comment?.moderation?.showWarning !== undefined) {
      setShowSensitiveContentCard(comment.moderation.showWarning);
    }
  }, [comment]);

  const hasChildren = children && children.length > 0 && depth < maxDepth;

  // Calculate connector positioning
  // The curve needs to reach back to where the parent's threadline is
  // Parent's threadline is at: -parentColumnWidth + parentAvatarCenter from this reply's left edge
  const connectorReachBack = parentColumnWidth - parentAvatarCenter;
  const curveWidth = connectorReachBack + REPLY_AVATAR_CENTER; // reach back + half of this avatar
  const curveHeight = 20;

  return (
    <Box className="sub-thread" pb="12px" _last={{ pb: 0 }} position="relative">
      {/* 
        YouTube-style structure:
        - sub-thread-threadline: connector column (curve + continuation)
        - sub-thread-content: the actual comment (avatar column + content)
      */}

      {/* Row wrapper (so sub-thread connectors don't span into nested children) */}
      <Box className="sub-thread-row">
        {/* Connector elements owned by this reply */}
        <Box
          className="sub-thread-threadline"
          position="absolute"
          left={`-${connectorReachBack}px`}
          top="0"
          bottom="0"
          width={`${curveWidth}px`}
          pointerEvents="none"
        >
          {/* Connection curve - from parent's threadline to this avatar */}
          <Box
            className="sub-thread-connection"
            position="absolute"
            left="0"
            top="0"
            width={`${curveWidth}px`}
            height={`${curveHeight}px`}
            borderLeft="2px solid"
            borderBottom="2px solid"
            borderColor="gray.300"
            borderBottomLeftRadius={`${curveHeight}px`}
          />

          {/* Continuation - vertical line for siblings below */}
          {!isLastChild && (
            <Box
              className="sub-thread-continuation"
              position="absolute"
              left="0"
              top="0"
              bottom="0"
              width="0"
              borderLeft="2px solid"
              borderColor="gray.300"
            />
          )}

          {/* Hitbox for hover detection */}
          <Box className="thread-hitbox" />
        </Box>

        {/* The actual comment content - repeats the same pattern as root */}
        <Box
          className="sub-thread-content"
          display="flex"
          pt={`${curveHeight - REPLY_AVATAR_SIZE / 2}px`}
        >
          {/* Avatar column - contains avatar + threadline (if has children) */}
          <Box
            className="author-thumbnail"
            width={`${REPLY_AVATAR_COLUMN_WIDTH}px`}
            flexShrink={0}
            position="relative"
            display="flex"
            justifyContent="center"
          >
            <Avatar
              w={`${REPLY_AVATAR_SIZE}px`}
              h={`${REPLY_AVATAR_SIZE}px`}
              src={comment.user.imgUrl || UserAvatar}
              borderRadius="full"
              cursor="pointer"
              onClick={(e) => handleUserClick(e, () => navigate(`/${comment.user.username}`))}
            />

            {/* YouTube-style segment (bounded to this comment row) */}
            {hasChildren && (
              <Box
                className="threadline"
                position="absolute"
                bottom="0"
                left="0"
                right="0"
                height={`calc(100% - ${REPLY_AVATAR_SIZE}px)`}
                pointerEvents="none"
              >
                <Box
                  className="continuation"
                  position="absolute"
                  right="0"
                  height="100%"
                  width="50%"
                  boxSizing="border-box"
                  borderLeft="2px solid"
                  borderColor="gray.300"
                />
                {/* Hitbox for hover detection */}
                <Box className="thread-hitbox" />
              </Box>
            )}
          </Box>

          {/* Content + nested children column */}
          <Box flex={1} minW={0} pb={3}>
            {/* Comment body */}
            {isEditing ? (
              <Box>
                <LexicalComposer initialConfig={initialConfig as any}>
                  <SharedHistoryContext>
                    <CommentEditor
                      onPost={handleEdit}
                      currentUser={currentUser}
                      articleId={articleId}
                      editorStateRef={editorStateRef}
                      isHiddenCheckbox
                      isDisabledCancelButton={false}
                      isEditing={isEditing}
                      isLoading={isCreatingComment}
                      onCancel={() => setIsEditing(false)}
                    />
                  </SharedHistoryContext>
                </LexicalComposer>
              </Box>
            ) : (
              <Box className="comment-body">
                <HStack justify="space-between">
                  <VStack align="start" spacing={0}>
                    <Box
                      fontWeight="medium"
                      display="flex"
                      flexWrap="wrap"
                      alignItems="center"
                      fontSize="sm"
                      gap={1}
                      cursor="pointer"
                      onClick={(e) =>
                        handleUserClick(e, () => navigate(`/${comment.user.username}`))
                      }
                    >
                      <span className="hover:text-growth-green-500 transition duration-200">
                        {comment.user.firstName}
                      </span>
                      {comment.user.isVerifiedAuthor && <Image ml={1} src={VerifiedIcon} alt="" />}
                      {comment.user.isFoundingCreator && (
                        <Image
                          ml={1}
                          src={FoundingCreatorIcon}
                          alt="Founding Creator"
                          title="Founding Creator"
                          w={4}
                          h={4}
                        />
                      )}
                      {comment.user.username && (
                        <span className="font-[600] text-xs text-02-dim-gray">
                          @{comment.user.username}
                        </span>
                      )}
                      {comment.userId === article?.creator?.id ? (
                        <Box
                          fontSize="xs"
                          height="20px"
                          lineHeight="14px"
                          bg="growth-green"
                          color="#EDF7EE"
                          px={2}
                          py={0.5}
                          borderRadius="full"
                        >
                          Author
                        </Box>
                      ) : (
                        currentUser?.id === comment.userId && (
                          <div className="px-2 py-0.5 rounded-[80px] bg-growth-green-50 text-xs text-growth-green-900 font-[600]">
                            You
                          </div>
                        )
                      )}
                      <div
                        className="relative ml-1"
                        onMouseEnter={() => !isTouchDevice && handleMouseEnter(comment.user.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick(comment.user.id);
                        }}
                        onMouseLeave={() => !isTouchDevice && handleMouseLeave()}
                      >
                        <RankBadge rank={comment?.user?.currentRank} hiddenRankText />
                        {openPopup && (
                          <div className="absolute top-[34px] md:top-[-20px] left-[-50%] md:left-[50px] z-10">
                            <WriterBadgeInfo
                              rank={comment?.user?.currentRank}
                              totalReceivedPoint={totalAwardedPoints.totalReceivedPoint}
                              totalReceivedUsers={totalAwardedPoints.totalReceivedUsers}
                            />
                          </div>
                        )}
                      </div>
                    </Box>
                    <Box fontSize="xs" color="dim-gray">
                      {convertTimeCreatedAt(comment.createdAt)} {comment.isEdited && '(edited)'}
                    </Box>
                  </VStack>
                  {!isDeleted && (
                    <Menu>
                      <MenuButton
                        as={Button}
                        variant="unstyled"
                        border="none"
                        boxShadow="none"
                        h="28px"
                        size="sm"
                        rounded="full"
                        _hover={{ bg: '#F0F0F0' }}
                      >
                        <MoreHorizontal className="ml-[6px]" size={18} />
                      </MenuButton>
                      {comment.userId === currentUser?.id ? (
                        <MenuList minW="unset" p="0.625rem" shadow="md" rounded="12px">
                          <MenuItem
                            _hover={{ bgColor: 'anti-flash-white' }}
                            rounded="0.5rem"
                            py="8px"
                            px="10px"
                            fontSize="sm"
                            fontWeight={900}
                            _focus={{ bgColor: 'anti-flash-white' }}
                            onClick={() => setIsEditing(true)}
                            icon={<img src={EditIcon} alt="" className="w-5 h-5" />}
                          >
                            Edit comment
                          </MenuItem>
                          <MenuItem
                            _hover={{ bgColor: 'anti-flash-white' }}
                            rounded="0.5rem"
                            py="8px"
                            px="10px"
                            fontSize="sm"
                            fontWeight={900}
                            _focus={{ bgColor: 'anti-flash-white' }}
                            icon={<img src={DeleteIcon} alt="" className="w-5 h-5" />}
                            onClick={() => setOpen(true)}
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      ) : (
                        <MenuList minW="unset" p="0.625rem" shadow="md" rounded="12px">
                          <MenuItem
                            _hover={{ bgColor: 'anti-flash-white' }}
                            rounded="0.5rem"
                            py="8px"
                            px="10px"
                            fontSize="sm"
                            fontWeight={900}
                            _focus={{ bgColor: 'anti-flash-white' }}
                            icon={<img src={FlagIcon} alt="" className="w-5 h-5" />}
                            onClick={() => setOpenReportModal(true)}
                          >
                            Report comment
                          </MenuItem>
                        </MenuList>
                      )}
                    </Menu>
                  )}
                </HStack>
              </Box>
            )}

            <Box>
              {!isEditing && (
                <Box color="jet-black" mt={1}>
                  {!isDeleted ? (
                    <Box>
                      {comment.parentUser && (
                        <Text
                          as="span"
                          color="growth-green"
                          fontSize="sm"
                          fontWeight={600}
                          cursor="pointer"
                          mr={1}
                          _hover={{ textDecoration: 'underline' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (comment.parentUser.username) {
                              navigate(`/${comment.parentUser.username}`);
                            }
                          }}
                        >
                          @{comment.parentUser.username || comment.parentUser.firstName}
                        </Text>
                      )}
                      <LexicalCommentViewer
                        content={comment.content}
                        className="min-h-0 text-sm inline"
                      />
                    </Box>
                  ) : (
                    <Text fontStyle="italic" color="dim-gray" fontWeight={400} fontSize="sm">
                      This comment has been deleted
                    </Text>
                  )}
                </Box>
              )}

              <div
                className={`${showSensitiveContentCard && !comment?.isShowSensitive ? 'block' : 'hidden'} absolute top-[20%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-10`}
              >
                <SensitiveContentCard
                  isComment
                  actionButton={comment?.moderation?.actionButton}
                  primarySource={comment?.moderation?.primarySource}
                  primaryViolation={comment?.moderation?.primaryViolation}
                  setShowSensitiveContentCard={setShowSensitiveContentCard}
                  commentId={comment?.id}
                  articleId={articleId}
                  isShowSensitive={comment?.isShowSensitive}
                />
              </div>

              {!isEditing && !isDeleted && (
                <HStack spacing={3} mt={1} flexWrap="wrap">
                  <CommentReactionButtons
                    comment={comment}
                    isReacting={false}
                    handleReaction={handleReaction}
                    reactionCounts={reactionCounts}
                    onOpenReactionModal={() => setIsReactionModalOpen(true)}
                  />
                  <Button
                    display="flex"
                    alignItems="center"
                    variant="unstyled"
                    border="none"
                    boxShadow="none"
                    height="auto"
                    fontSize="xs"
                    _hover={{ opacity: 0.7 }}
                    onClick={() => setIsReplying(!isReplying)}
                  >
                    <MessageSquareText size={14} />
                    <Text ml={1}>Reply</Text>
                  </Button>
                  <ReputationPointComponent
                    reputationPoints={comment.reputationPoint}
                    setOpenAwardWriterModal={setOpenAwardWriterModal}
                  />
                </HStack>
              )}

              {isReplying && (
                <Box mt={2}>
                  <LexicalComposer initialConfig={initialConfig as any}>
                    <SharedHistoryContext>
                      <CommentEditor
                        onPost={() => handleReply(comment.id)}
                        currentUser={currentUser}
                        editorStateRef={editorStateRef}
                        isHiddenCheckbox
                        replyTo={comment.user.username}
                        isLoading={isCreatingComment}
                        isDisabledCancelButton={false}
                        onCancel={() => setIsReplying(false)}
                      />
                    </SharedHistoryContext>
                  </LexicalComposer>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Nested children - same recursive pattern (outside the row, like YouTube's replies block) */}
      {hasChildren && (
        <Box className="replies-container" mt={0} ml={`${REPLY_AVATAR_COLUMN_WIDTH}px`}>
          {children.map((child, index) => (
            <ThreadedReplyItem
              key={child.id}
              comment={child}
              currentUser={currentUser}
              createComment={createComment}
              updateComment={updateComment}
              deleteComment={deleteComment}
              reactToComment={reactToComment}
              fetchReplies={fetchReplies}
              refetchComments={refetchComments}
              article={article}
              articleId={articleId}
              nodeId={nodeId}
              convertLexicalScope={convertLexicalScope}
              modalType={modalType}
              depth={child.depth || depth + 1}
              maxDepth={maxDepth}
              onReplyAdded={onReplyAdded}
              isLastChild={index === children.length - 1}
              parentAvatarCenter={REPLY_AVATAR_COLUMN_WIDTH / 2}
              parentColumnWidth={REPLY_AVATAR_COLUMN_WIDTH}
            />
          ))}
        </Box>
      )}

      {/* Modals */}
      {open ? (
        modalType === 'general' ? (
          <CustomModal
            isOpen
            title="Delete Comment?"
            onClick={() => setOpen(false)}
            hideFooter
            maxW="407px"
          >
            <div className="w-full flex flex-col items-center">
              <img src={DeleteModalIcon} alt="" />
              <p className="text-lg font-[600] mt-4 text-center px-6">
                This action will permanently delete your comment
              </p>
              <div className="flex gap-[10px] mt-6 w-full">
                <Button
                  bg="white"
                  border="2px solid"
                  borderColor="growth-green"
                  rounded="8px"
                  w="100%"
                  onClick={() => setOpen(false)}
                  h="42px"
                  color="growth-green"
                  fontWeight={900}
                  fontSize="md"
                  _hover={{ opacity: '0.9' }}
                >
                  Cancel
                </Button>
                <Button
                  bg="error-red"
                  color="white"
                  border="1px solid"
                  borderColor="error-red"
                  rounded="8px"
                  w="100%"
                  h="42px"
                  onClick={handleDelete}
                  fontWeight={900}
                  fontSize="md"
                  _hover={{ opacity: '0.7' }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </CustomModal>
        ) : (
          <ConfirmDeleteModal setOpen={setOpen} handleDelete={handleDelete} />
        )
      ) : null}

      {openReportModal ? (
        modalType === 'general' ? (
          <Modal isOpen={openReportModal} isCentered onClose={() => setOpenReportModal(false)}>
            <ModalOverlay />
            <ModalContent h="80vh" maxW="800px">
              <ModalBody p={0} overflow="auto" rounded="12px">
                <ReportModal
                  commentId={comment.id}
                  targetUserId={comment.userId}
                  username={comment.user.username}
                  setOpen={setOpenReportModal}
                />
              </ModalBody>
            </ModalContent>
          </Modal>
        ) : (
          <Box
            bg="white"
            position="absolute"
            top="0"
            right="0"
            bottom="0"
            zIndex="1000"
            w="100%"
            h="100%"
          >
            <ReportModal
              commentId={comment.id}
              targetUserId={comment.userId}
              username={comment.user.username}
              setOpen={setOpenReportModal}
            />
          </Box>
        )
      ) : null}

      {openAwardWriterModal ? (
        modalType === 'general' ? (
          <AwardWriterModal
            isOpen
            handleCreatePoint={handleCreatePoint}
            title={`Recognize ${comment.user.username || comment.user.firstName}'s comment`}
            onClose={() => setOpenAwardWriterModal(false)}
            isLoading={isCreatingPoint}
            previousPoint={comment?.reputationPoint?.given}
            alreadyGivePoint={comment?.reputationPoint?.given !== 0}
            dailyLimits={comment?.reputationPoint?.dailyLimits}
            contentType="comment"
          />
        ) : (
          <AwardModalOnSidePanel
            setOpen={setOpenAwardWriterModal}
            handleCreatePoint={handleCreatePoint}
            onClose={() => setOpenAwardWriterModal(false)}
            isLoading={isCreatingPoint}
            previousPoint={comment?.reputationPoint?.given}
            alreadyGivePoint={comment?.reputationPoint?.given !== 0}
            title={`Recognize ${comment.user.username || comment.user.firstName}'s comment:`}
            dailyLimits={comment?.reputationPoint?.dailyLimits}
          />
        )
      ) : null}

      <CommentReactionModal
        isOpen={isReactionModalOpen}
        onClose={() => setIsReactionModalOpen(false)}
        commentId={comment.id}
        totalReactions={comment.reactionNumber || 0}
      />
    </Box>
  );
};

export default ThreadedReplyItem;
